import {pick} from 'lodash-es';
import {useEffect, useReducer, useState} from 'react';

import {merchantService} from '@/services/merchants';
import type {BusinessPoolAccount} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {BusinessPoolAccountValidator} from '@/validators/BusinessPoolAccountValidator';

import {PoolAccountActionType} from './types';
import updatePoolAccountReducer from './updatePoolAccountReducer';

const usePoolAccountUpdate = (
  onClose: VoidFunction,
  initialData: Partial<BusinessPoolAccount> = {
    meta: {
      flags: {
        isDeactivated: false,
      },
    },
  },
) => {
  const [poolAccount, dispatch] = useReducer(
    updatePoolAccountReducer,
    initialData as BusinessPoolAccount,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch({
      type: PoolAccountActionType.UPDATE_POOL_ACCOUNT,
      payload: initialData,
    });
  }, []);

  const handleSubmit = async (rawPoolAccount: BusinessPoolAccount) => {
    setIsLoading(true);
    try {
      const validPoolAccount = new BusinessPoolAccountValidator(
        rawPoolAccount,
      ).value();
      if (rawPoolAccount.id) {
        const fieldsToInclude: (keyof BusinessPoolAccount)[] = [
          'accountName',
          'accountNumber',
          'institutionName',
          'countryCode',
          'currencyCode',
          'channel',
          'meta',
        ];
        await merchantService.updateBusinessPoolAccount(
          rawPoolAccount.id,
          pick(validPoolAccount, fieldsToInclude),
        );
        showSuccessMessage(`Business pool account updated successfully`);
      } else {
        await merchantService.createBusinessPoolAccount(validPoolAccount);
        showSuccessMessage(`Business pool account saved successfully`);
      }

      onClose();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      showErrorMessage(
        `Error saving pool account ${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    dispatch,
    handleSubmit,
    isLoading,
    poolAccount,
  };
};

export default usePoolAccountUpdate;
